import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Flex,
  Form,
  Grid,
  Input,
  theme,
  Typography,
} from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import UserGroupOutlined from "assets/icons/user-group-outline.svg";
import { useVatNumber } from "hooks/integrations";
import { useTahseelFormSubmit } from "hooks/tahseel";
import { TahseelFormData } from "types/tahseel";
import ContactUs from "../Integrations/ContactUs";
import FileUploader from "../Integrations/FileUploader";
import QIcon from "../QIcon";
import Feedback from "./Feedback";
import { isRtl } from "utils/locale";

const { Link, Paragraph } = Typography;

const TahseelDrawer = () => {
  const { token } = theme.useToken();
  const { i18n, t } = useTranslation();
  const screens = Grid.useBreakpoint();

  const [open, setOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const [form] = Form.useForm<TahseelFormData>();
  const values = Form.useWatch([], form);

  const { vatNumber, vatIsSuccess } = useVatNumber({ enabled: open });
  const { handleSubmit, isLoading, isSuccess, isError } = useTahseelFormSubmit({ form });

  const drawerWidth = screens.lg ? "43%" : "100%";

  const onClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    document.addEventListener("triggerTahseelDrawer", handleOpen);
    return () => {
      document.removeEventListener("triggerTahseelDrawer", handleOpen);
    };
  }, []);

  useEffect(() => {
    if (vatNumber && vatIsSuccess) {
      form.setFieldValue("vat_number", vatNumber);
    }
  }, [vatNumber]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsFormValid(true))
      .catch(() => setIsFormValid(false));
  }, [form, values]);

  if (isSuccess && !isLoading) {
    return <Feedback shouldOpen={true} status="success" />;
  }

  if (isError && !isLoading) {
    return <Feedback shouldOpen={true} status="error" />;
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={drawerWidth}
      title={t("templates.integrations.tahseel_activation.title")}
      placement={isRtl() ? "left" : "right"}
    >
      <Paragraph type="secondary">
        {t("templates.integrations.tahseel_activation.content")}
      </Paragraph>
      <Form form={form} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} onFinish={handleSubmit}>
        <FileUploader
          name="commercial_document"
          label={t("templates.integrations.tahseel_activation.form_info.commercial_registration")}
          note={t(
            "templates.integrations.tahseel_activation.form_info.commercial_registration_note",
          )}
          required={true}
          allowPDF={true}
        />
        <FileUploader
          name="authorization_letter"
          label={t("templates.integrations.tahseel_activation.form_info.authorization_letter")}
          note={t("templates.integrations.tahseel_activation.form_info.authorization_letter_note")}
          required={true}
          allowPDF={true}
        />
        <Form.Item
          name="vat_number"
          label={t("templates.integrations.tahseel_activation.form_info.vat_number")}
          rules={[
            { required: true },
            {
              pattern: /^3\d{14}$/,
              message: t("templates.integrations.tahseel_activation.form_info.vat_number_invalid"),
            },
          ]}
        >
          <Input
            size="large"
            placeholder={t(
              "templates.integrations.tahseel_activation.form_info.vat_number_placeholder",
            )}
            disabled={vatNumber ? true : false}
          />
        </Form.Item>
        <Form.Item
          name="iban_number"
          label={t("templates.integrations.tahseel_activation.form_info.iban")}
          rules={[
            { required: true },
            {
              pattern: /^SA\d{22}$/,
              message: t("templates.integrations.tahseel_activation.form_info.iban_invalid"),
            },
          ]}
        >
          <Input
            size="large"
            placeholder={t("templates.integrations.tahseel_activation.form_info.iban_placeholder")}
          />
        </Form.Item>
        <Alert
          description={
            <Paragraph>
              <QIcon
                component={UserGroupOutlined}
                style={{
                  color: "#fff",
                  fontSize: token.fontSizeXL,
                  marginInlineEnd: token.marginXS,
                }}
              />
              {t("templates.integrations.tahseel_activation.form_info.fees_description")}
            </Paragraph>
          }
          style={{
            marginBottom: token.marginLG,
            backgroundColor: token.colorFillQuaternary,
            borderColor: token.colorBorder,
          }}
        />
        <Form.Item
          name="terms_and_conditions"
          rules={[
            {
              required: true,
              message: t(
                "templates.integrations.tahseel_activation.form_info.terms_and_conditions_error",
              ),
            },
          ]}
          valuePropName="checked"
          getValueFromEvent={(e: CheckboxChangeEvent) => {
            const value = e.target.checked;
            if (value === false) return undefined;
            return value;
          }}
        >
          <Checkbox style={{ fontWeight: "normal" }}>
            <Trans
              i18nKey="templates.integrations.tahseel_activation.form_info.terms_and_conditions"
              components={{
                a: (
                  <Link
                    href={
                      i18n.language === "ar"
                        ? "/ara/qtahseel-basic-terms-and-conditions-2"
                        : "/en/qtahseel-basic-terms-and-conditions"
                    }
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </Checkbox>
        </Form.Item>
        <Divider />
        <Flex gap={token.margin}>
          <Button size="large" style={{ flex: 1 }} onClick={onClose} disabled={isLoading}>
            {t("forms.buttons.cancel")}
          </Button>
          <Button
            size="large"
            style={{ flex: 1 }}
            type="primary"
            htmlType="submit"
            disabled={isLoading || !form.isFieldsTouched() || !isFormValid}
            loading={isLoading}
          >
            {t("forms.buttons.activate_now")}
          </Button>
        </Flex>
        <ContactUs />
      </Form>
    </Drawer>
  );
};

export default TahseelDrawer;
